import { reactive, unref } from 'vue'
import posthog from 'posthog-js'

let analytics = reactive({})

// During Meticulous test replay, all flags are forced to true so that old recorded
// sessions exercise flag-gated code paths and don't generate false positives.
// window.Meticulous?.isRunningAsTest is only set during replay, never in production.
// See: https://app.meticulous.ai/docs/how-to/testing-feature-flags
const isMeticulousReplay = !!globalThis.Meticulous?.isRunningAsTest

// camelCase flag -> PostHog key. Add new flags here only.
const FLAG_KEYS = {
    rateTiers: 'payor-rate-tiers',
    pslAverageRates: 'psl-average-rates',
    pslCustomUtilizations: 'psl-custom-utilizations',
    signalSearchAI: 'signal-search-ai',
    hptFields: 'hpt-fields',
    claimsFields: 'claims-fields',
    employerReports: 'employer-reports',
    pslAnchor: 'psl-anchor',
    pslCommonCodes: 'psl-common-codes',
    ssoOptions: 'sso-options',
    signalWatch: 'signal-watch',
    signalWatchNotifications: 'signal-watch-notifications',
    signalWatchPeerRates: 'signal-watch-peer-rates',
    signalWatchHospitalPeerServiceLines: 'signal-watch-hospital-peer-service-lines',
    signalEinUtilization: 'signal-ein-utilization',
}

// All flags default off; Meticulous replay forces them on.
const featureFlags = reactive(Object.fromEntries(Object.keys(FLAG_KEYS).map((key) => [key, isMeticulousReplay])))

// Local-only: e.g. 'signal-watch' -> VITE_FF_SIGNAL_WATCH=true
export const localFlagEnvName = (phKey) => `VITE_FF_${phKey.toUpperCase().replace(/-/g, '_')}`

export function parseLocalFeatureFlags(env = {}) {
    return Object.fromEntries(
        Object.entries(FLAG_KEYS).map(([key, phKey]) => [key, env[localFlagEnvName(phKey)] === 'true'])
    )
}

export function useAnalytics() {
    async function init() {
        if (Object.hasOwn(analytics, 'track')) return

        if (import.meta.env.VITE_ANALYTICS_ENABLED !== 'true' || !import.meta.env.VITE_POSTHOG_API_KEY) {
            console.warn('Analytics disabled; feature flags from VITE_FF_* env vars')
            if (!isMeticulousReplay) {
                Object.assign(featureFlags, parseLocalFeatureFlags(import.meta.env))
            }

            return unref(
                Object.assign(analytics, {
                    identify: () => true,
                    track: (action, params) => console.log('analytics:track', action, params),
                    page: () => true,
                    captureException: (error) => console.error('analytics:captureException', error),
                })
            )
        }

        posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
            api_host: 'https://us.i.posthog.com',
            person_profiles: 'always',
            autocapture: false,
            // Without `defaults`, posthog-js only captures the initial load; SPA route changes need history_change.
            capture_pageview: 'history_change',
            capture_exceptions: true,
        })

        posthog.onFeatureFlags(() => {
            if (isMeticulousReplay) return
            for (const [key, phKey] of Object.entries(FLAG_KEYS)) {
                featureFlags[key] = !!posthog.isFeatureEnabled(phKey)
            }
        })

        Object.assign(analytics, {
            identify: (userId, { email, name, account, account_id }) => {
                posthog.identify(userId, { email, name, account, account_id, userId })

                // Reload feature flags after identifying to ensure they're loaded
                posthog.reloadFeatureFlags()
            },

            // Fallback. now autocaptured by  postgo
            page: (path, properties) =>
                posthog.capture('$pageview', {
                    path,
                    ...properties,
                }),
            track: ({ event, properties }) =>
                posthog.capture(event, {
                    ...properties,
                }),
            captureException: (error, properties) => posthog.captureException(error, properties),
        })
    }

    init()

    return {
        analytics,
        featureFlags,
    }
}
